import { useEffect, useState } from 'react';
import type { FileNode, TreeNode } from '../../agents/userFilesApi.ts';
import PanelEmpty from './PanelEmpty.tsx';
import './FilesPanel.css';

interface FilesPanelProps {
  tree: readonly TreeNode[];
  loading: boolean;
  error: string | null;
  /** Open a file: routes to chat and asks the agent for a download link. */
  onOpenFile: (path: string) => void;
  /** Delete a file: routes to chat where the agent surfaces a Yes/No confirm. */
  onDeleteFile: (path: string) => void;
}

/** Inspector "Files" section: the signed-in user's documents as an expandable
 *  folder tree. Clicking a filename asks the agent (in chat) for a download
 *  link; the per-row 3-dot menu's Delete routes to chat too, where the agent's
 *  confirmation card is the single Yes/No gate. */
export default function FilesPanel({
  tree,
  loading,
  error,
  onOpenFile,
  onDeleteFile,
}: FilesPanelProps) {
  // Folders are expanded by default; this holds the ones the user has collapsed
  // (file counts are small, so showing everything up front is friendliest).
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  // Which file row's 3-dot menu is open (by path), or null.
  const [menuPath, setMenuPath] = useState<string | null>(null);

  // Close an open row menu on any outside click / Escape.
  useEffect(() => {
    if (menuPath === null) {
      return;
    }
    const close = (): void => setMenuPath(null);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setMenuPath(null);
      }
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuPath]);

  const toggleFolder = (path: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  let body;
  if (loading) {
    body = <div className="om-files__status">Loading your files…</div>;
  } else if (error !== null) {
    body = <div className="om-files__status om-files__status--error">{error}</div>;
  } else if (tree.length === 0) {
    body = <PanelEmpty>Files you ask the agent to save will appear here.</PanelEmpty>;
  } else {
    body = (
      <div className="om-files__list" role="tree">
        {tree.map((node) => (
          <FileTreeNode
            key={node.path || node.name}
            node={node}
            depth={0}
            collapsed={collapsed}
            menuPath={menuPath}
            onToggleFolder={toggleFolder}
            onOpenFile={onOpenFile}
            onDeleteFile={onDeleteFile}
            onMenu={setMenuPath}
          />
        ))}
      </div>
    );
  }

  return <section className="om-files">{body}</section>;
}

interface FileTreeNodeProps {
  node: TreeNode;
  depth: number;
  collapsed: Set<string>;
  menuPath: string | null;
  onToggleFolder: (path: string) => void;
  onOpenFile: (path: string) => void;
  onDeleteFile: (path: string) => void;
  onMenu: (path: string | null) => void;
}

function FileTreeNode({
  node,
  depth,
  collapsed,
  menuPath,
  onToggleFolder,
  onOpenFile,
  onDeleteFile,
  onMenu,
}: FileTreeNodeProps) {
  // Indent each level; CSS custom prop keeps the math out of inline width.
  const indent = { '--om-files-depth': String(depth) } as React.CSSProperties;

  if (node.type === 'folder') {
    const isOpen = !collapsed.has(node.path);
    return (
      <div className="om-files__folder" role="treeitem" aria-expanded={isOpen} style={indent}>
        <button
          type="button"
          className="om-files__row om-files__row--folder"
          onClick={() => onToggleFolder(node.path)}
        >
          <CaretIcon open={isOpen} />
          <FolderIcon open={isOpen} />
          <span className="om-files__name">{node.name}</span>
        </button>
        {isOpen ? (
          <div className="om-files__children" role="group">
            {node.children.map((child) => (
              <FileTreeNode
                key={child.path || child.name}
                node={child}
                depth={depth + 1}
                collapsed={collapsed}
                menuPath={menuPath}
                onToggleFolder={onToggleFolder}
                onOpenFile={onOpenFile}
                onDeleteFile={onDeleteFile}
                onMenu={onMenu}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  const menuOpen = menuPath === node.path;
  const expiry = formatExpiry(node);
  return (
    <div className="om-files__file" role="treeitem" style={indent}>
      <button
        type="button"
        className="om-files__row om-files__row--file"
        title={`Download ${node.path}`}
        onClick={() => onOpenFile(node.path)}
      >
        {/* Spacer keeps file icons aligned under their folder's contents (where
            a folder's caret sits). */}
        <span className="om-files__caret-spacer" aria-hidden="true" />
        <FileIcon />
        <span className="om-files__name">{node.name}</span>
        <span className="om-files__size">{formatBytes(node.bytes)}</span>
        {expiry !== null ? (
          <span
            className="om-files__expiry"
            title="This file is temporary and will be deleted automatically. Ask the agent to save a copy if you want to keep it."
          >
            {expiry}
          </span>
        ) : null}
      </button>
      <div className="om-files__menu-wrap">
        <button
          type="button"
          className="om-files__menu-btn"
          aria-label={`Actions for ${node.name}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => {
            // Stop the document click-away handler from immediately closing it.
            e.stopPropagation();
            onMenu(menuOpen ? null : node.path);
          }}
        >
          ⋮
        </button>
        {menuOpen ? (
          <div className="om-files__menu" role="menu">
            <button
              type="button"
              className="om-files__menu-item om-files__menu-item--danger"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                onMenu(null);
                onDeleteFile(node.path);
              }}
            >
              Delete…
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Expand/collapse caret for a folder row — points right when collapsed,
 *  rotates down (via CSS) when open. The primary expand affordance. */
function CaretIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`om-files__caret${open ? ' om-files__caret--open' : ''}`}
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Folder glyph — a closed folder, or an open folder when expanded. */
function FolderIcon({ open }: { open: boolean }) {
  return (
    <svg
      className="om-files__icon om-files__icon--folder"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      aria-hidden="true"
    >
      {open ? (
        <path
          d="M4 19l2.2-7a1 1 0 0 1 1-.7H21l-2.2 7a1 1 0 0 1-1 .7H4Zm0 0V6a1 1 0 0 1 1-1h4l2 2h7a1 1 0 0 1 1 1v2.3"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M4 6a1 1 0 0 1 1-1h4l2 2h8a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/** Document glyph (page with a folded corner) for a file row. */
function FileIcon() {
  return (
    <svg
      className="om-files__icon om-files__icon--file"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M13 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8l-5-5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M13 3v5h5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * "expires today" / "expires in 6d" from an ISO expiry, or null when the file
 * is persistent.
 *
 * Ephemeral files (emailed attachments, console uploads) are deleted by an S3
 * lifecycle rule after 7 days. The backend has always reported this; until now
 * nothing in the UI showed it, so those files simply vanished. A user who is
 * not told loses work with no warning at all.
 */
function formatExpiry(node: FileNode): string | null {
  if (node.ephemeral !== true || node.expiresAt === undefined) {
    return null;
  }
  const ms = Date.parse(node.expiresAt) - Date.now();
  if (!Number.isFinite(ms)) {
    return null;
  }
  const days = Math.floor(ms / (24 * 3600 * 1000));
  return days <= 0 ? 'expires today' : `expires in ${days}d`;
}

/** Compact human byte size, e.g. 0 B, 512 B, 1.2 KB, 3.4 MB. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
